import React, { useEffect, useMemo, useRef, useState } from 'react';

type ImageState = {
  file?: File | null;
  url?: string;
  imgEl?: HTMLImageElement | null;
};

const brandOrange = '#4F6660';
const brandNavy = '#0A2342';

const loadImageElement = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const MockupMaker: React.FC = () => {
  const [boxFront, setBoxFront] = useState<ImageState>({});
  const [discOne, setDiscOne] = useState<ImageState>({});
  const [discTwo, setDiscTwo] = useState<ImageState>({});

  const [backgroundColor, setBackgroundColor] = useState<string>('#ffffff');
  const [sideColor, setSideColor] = useState<string>(brandOrange);
  const [shadow, setShadow] = useState<boolean>(true);
  const [reflections, setReflections] = useState<boolean>(true);
  const [depth, setDepth] = useState<number>(70); // 0-100 visual depth
  const [exportScale, setExportScale] = useState<number>(2); // 2x by default
  const [title, setTitle] = useState<string>('SOFTWARE');
  const [subtitle, setSubtitle] = useState<string>('BOX MOCKUP');
  const [version, setVersion] = useState<string>('1.5');

  const previewRef = useRef<HTMLDivElement>(null);

  // Load <img> elements for canvas export when URLs change
  useEffect(() => {
    const run = async () => {
      if (boxFront.url) {
        try {
          const img = await loadImageElement(boxFront.url);
          setBoxFront(prev => ({ ...prev, imgEl: img }));
        } catch {}
      }
      if (discOne.url) {
        try {
          const img1 = await loadImageElement(discOne.url);
          setDiscOne(prev => ({ ...prev, imgEl: img1 }));
        } catch {}
      }
      if (discTwo.url) {
        try {
          const img2 = await loadImageElement(discTwo.url);
          setDiscTwo(prev => ({ ...prev, imgEl: img2 }));
        } catch {}
      }
    };
    run();
  }, [boxFront.url, discOne.url, discTwo.url]);

  const handleFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<ImageState>>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setter({ file, url, imgEl: null });
  };

  const clearAll = () => {
    setBoxFront({});
    setDiscOne({});
    setDiscTwo({});
  };

  // Canvas export that approximates the DOM preview
  const exportPNG = async () => {
    // Base logical size
    const width = 1200;
    const height = 800;
    const scale = Math.max(1, Math.min(4, exportScale));
    const canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(scale, scale);
    // Background
    ctx.fillStyle = backgroundColor || '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Ground shadow
    if (shadow) {
      const g = ctx.createRadialGradient(width * 0.45, height * 0.78, 30, width * 0.45, height * 0.85, 260);
      g.addColorStop(0, 'rgba(0,0,0,0.18)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, height * 0.6, width, height * 0.4);
    }

    // Box dimensions
    const boxX = 260;
    const boxY = 150;
    const boxW = 320;
    const boxH = 460;
    const sideW = 60;
    const skew = -0.35; // side skew

    // Draw front (with image or fallback)
    ctx.save();
    // Shadow for the box
    if (shadow) {
      ctx.shadowColor = 'rgba(0,0,0,0.18)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 12;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.restore();
    ctx.strokeStyle = 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 1;
    ctx.strokeRect(boxX, boxY, boxW, boxH);

    if (boxFront.imgEl) {
      // Cover fit
      const img = boxFront.imgEl;
      const arImg = img.width / img.height;
      const arBox = boxW / boxH;
      let dw = boxW, dh = boxH, dx = boxX, dy = boxY;
      if (arImg > arBox) {
        dh = boxH;
        dw = dh * arImg;
        dx = boxX - (dw - boxW) / 2;
      } else {
        dw = boxW;
        dh = dw / arImg;
        dy = boxY - (dh - boxH) / 2;
      }
      ctx.drawImage(img, dx, dy, dw, dh);
    } else {
      // Placeholder design
      ctx.fillStyle = '#f7f7f7';
      ctx.fillRect(boxX, boxY, boxW, boxH);
      ctx.fillStyle = brandNavy;
      ctx.font = 'bold 46px Poppins, system-ui, Arial';
      ctx.textAlign = 'center';
      ctx.fillText(title || 'SOFTWARE', boxX + boxW / 2, boxY + boxH / 2 - 10);
      ctx.fillStyle = '#888';
      ctx.font = 'bold 22px Poppins, system-ui, Arial';
      ctx.fillText(subtitle || 'BOX MOCKUP', boxX + boxW / 2, boxY + boxH / 2 + 30);
    }

    // Draw side face
    ctx.save();
    ctx.fillStyle = sideColor;
    ctx.transform(1, 0, skew, 1, boxX + boxW, 0);
    ctx.fillRect(0, boxY + 8, sideW, boxH - 16);
    // Side lighting
    const sideGrad = ctx.createLinearGradient(0, boxY + 8, sideW, boxY + boxH - 16);
    sideGrad.addColorStop(0, 'rgba(255,255,255,' + (0.22 * depth/100) + ')');
    sideGrad.addColorStop(0.5, 'rgba(255,255,255,0)');
    sideGrad.addColorStop(1, 'rgba(0,0,0,' + (0.12 * depth/100) + ')');
    ctx.fillStyle = sideGrad;
    ctx.fillRect(0, boxY + 8, sideW, boxH - 16);
    ctx.restore();

    // Version badge on front
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(0,0,0,0.06)';
    ctx.lineWidth = 1;
    const badgeX = boxX + 18;
    const badgeY = boxY + 22;
    const badgeW = 56;
    const badgeH = 28;
    ctx.fillRect(badgeX, badgeY, badgeW, badgeH);
    ctx.strokeRect(badgeX, badgeY, badgeW, badgeH);
    ctx.fillStyle = '#999';
    ctx.font = 'bold 16px Poppins, system-ui, Arial';
    ctx.textAlign = 'center';
    ctx.fillText(version || '1.5', badgeX + badgeW / 2, badgeY + 20);

    // Front face subtle highlight for 3D
    const frontHighlight = ctx.createLinearGradient(boxX, boxY, boxX, boxY + boxH);
    frontHighlight.addColorStop(0, 'rgba(255,255,255,' + (0.15 * depth/100) + ')');
    frontHighlight.addColorStop(0.4, 'rgba(255,255,255,0)');
    frontHighlight.addColorStop(1, 'rgba(0,0,0,' + (0.06 * depth/100) + ')');
    ctx.fillStyle = frontHighlight;
    ctx.fillRect(boxX, boxY, boxW, boxH);

    // Discs
    const drawDisc = (x: number, y: number, r: number, img?: HTMLImageElement | null, opts?: { forReflection?: boolean }) => {
      // Outer shadow
      if (shadow && !opts?.forReflection) {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.18)';
        ctx.shadowBlur = 18;
        ctx.shadowOffsetY = 8;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.restore();
      }

      // Label area
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      if (img) {
        // cover fit for circle
        const ar = img.width / img.height;
        const s = r * 2;
        let dw = s, dh = s, dx = x - r, dy = y - r;
        const arBox2 = 1; // square
        if (ar > arBox2) {
          dh = s;
          dw = dh * ar;
          dx = x - dw / 2;
          dy = y - dh / 2;
        } else {
          dw = s;
          dh = dw / ar;
          dx = x - dw / 2;
          dy = y - dh / 2;
        }
        ctx.drawImage(img, dx, dy, dw, dh);
      } else {
        const g = ctx.createLinearGradient(x - r, y - r, x + r, y + r);
        g.addColorStop(0, '#ffb380');
        g.addColorStop(1, sideColor);
        ctx.fillStyle = g;
        ctx.fillRect(x - r, y - r, r * 2, r * 2);
      }
      ctx.restore();

      if (!opts?.forReflection) {
        // Specular highlight
        const hi = ctx.createRadialGradient(x - r*0.3, y - r*0.35, r*0.1, x - r*0.3, y - r*0.35, r*0.9);
        hi.addColorStop(0, 'rgba(255,255,255,' + (0.35 * depth/100) + ')');
        hi.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = hi;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();

        // Inner ring (hole)
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, r * 0.22, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fillStyle = '#d1d5db';
        ctx.fill();
        ctx.restore();
      }
    };

    drawDisc(800, 470, 130, discTwo.imgEl);
    drawDisc(700, 530, 150, discOne.imgEl);

    // Reflections
    if (reflections) {
      const refAlpha = 0.28 * (depth/100);
      // Box reflection
      const refH = 120;
      const refGrad = ctx.createLinearGradient(boxX, boxY + boxH + 10, boxX, boxY + boxH + 10 + refH);
      refGrad.addColorStop(0, 'rgba(0,0,0,' + (refAlpha * 0.25) + ')');
      refGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = refGrad;
      ctx.fillRect(boxX, boxY + boxH + 10, boxW, refH);

      // Disc reflections (soft)
      const drawDiscReflection = (x:number, y:number, r:number, img?:HTMLImageElement|null)=>{
        ctx.save();
        ctx.globalAlpha = refAlpha;
        drawDisc(x, y + r*1.2, r*0.95, img, { forReflection: true });
        // Fade out
        const mask = ctx.createLinearGradient(x, y + r*0.9, x, y + r*2.2);
        mask.addColorStop(0, 'rgba(0,0,0,1)');
        mask.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'destination-in';
        ctx.fillStyle = mask;
        ctx.beginPath();
        ctx.rect(x - r*1.2, y + r*0.8, r*2.4, r*1.6);
        ctx.fill();
        ctx.restore();
        ctx.globalCompositeOperation = 'source-over';
      };

      drawDiscReflection(800, 470, 130, discTwo.imgEl);
      drawDiscReflection(700, 530, 150, discOne.imgEl);
    }

    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `mockup-${Date.now()}.png`;
    a.click();
  };

  const StepLabel: React.FC<{ label: string; icon: string }>=({ label, icon })=> (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">{icon}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🎨 Mockup Maker</h1>
        <p className="text-gray-600">Upload je afbeeldingen en download direct een PNG van de mockup.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Controls */}
        <div className="lg:col-span-1 bg-white rounded-lg border p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Box voorkant (afbeelding)</label>
            <input type="file" accept="image/*" onChange={(e)=>handleFile(e, setBoxFront)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Disc 1 label</label>
              <input type="file" accept="image/*" onChange={(e)=>handleFile(e, setDiscOne)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Disc 2 label</label>
              <input type="file" accept="image/*" onChange={(e)=>handleFile(e, setDiscTwo)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Achtergrond</label>
              <input type="color" className="w-full h-10 rounded" value={backgroundColor} onChange={(e)=>setBackgroundColor(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Box zijkleur</label>
              <input type="color" className="w-full h-10 rounded" value={sideColor} onChange={(e)=>setSideColor(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Titel</label>
              <input value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full border rounded px-2 py-1" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitel</label>
              <input value={subtitle} onChange={(e)=>setSubtitle(e.target.value)} className="w-full border rounded px-2 py-1" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Versie</label>
              <input value={version} onChange={(e)=>setVersion(e.target.value)} className="w-full border rounded px-2 py-1" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={shadow} onChange={(e)=>setShadow(e.target.checked)} /> Schaduw
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={reflections} onChange={(e)=>setReflections(e.target.checked)} /> Reflecties
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              Diepte
              <input type="range" min={0} max={100} value={depth} onChange={(e)=>setDepth(Number(e.target.value))} />
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              Export schaal
              <select value={exportScale} onChange={(e)=>setExportScale(Number(e.target.value))} className="border rounded px-2 py-1">
                <option value={1}>1x</option>
                <option value={2}>2x</option>
                <option value={3}>3x</option>
                <option value={4}>4x</option>
              </select>
            </label>
          </div>

          <div className="flex gap-2">
            <button onClick={exportPNG} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">⬇️ Download PNG</button>
            <button onClick={clearAll} className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Reset</button>
          </div>

          <div className="pt-2 text-xs text-gray-500">
            Tip: gebruik vierkante labels voor discs en een staande afbeelding voor de box.
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          <div
            ref={previewRef}
            className="relative w-full aspect-[3/2] rounded-lg border overflow-hidden"
            style={{ background: backgroundColor }}
          >
            {/* Shadow ellipse */}
            {shadow && (
              <div
                className="absolute left-1/2 -translate-x-1/2 bottom-16 w-[70%] h-40"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 70%)'
                }}
              />
            )}

            {/* Box group */}
            <div className="absolute left-[16%] top-[14%] h-[60%]" style={{ width: '28%' }}>
              {/* Side face */}
              <div
                className="absolute right-[-10%] top-[1%] h-[98%] w-[14%]"
                style={{ background: sideColor, transform: 'skewY(-18deg)' }}
              />
              {/* Front face */}
              <div className="relative h-full w-full bg-white shadow-lg border border-gray-100 overflow-hidden">
                {boxFront.url ? (
                  <img src={boxFront.url} alt="box" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                    <div className="text-3xl font-bold text-[color:var(--navy,#0A2342)]">{title}</div>
                    <div className="text-gray-500 font-semibold">{subtitle}</div>
                  </div>
                )}
                <div className="absolute left-3 top-3 bg-white/90 border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 font-semibold">
                  {version}
                </div>
              </div>
            </div>

            {/* Discs */}
            <div className="absolute right-[18%] top-[40%] w-[22%] aspect-square rounded-full shadow-xl border border-gray-100 overflow-hidden">
              {discTwo.url ? (
                <img src={discTwo.url} alt="disc" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full" style={{ background: `linear-gradient(135deg, #ffb380, ${sideColor})` }} />
              )}
              {/* inner ring */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[22%] aspect-square bg-gray-300 rounded-full" />
              </div>
            </div>

            <div className="absolute right-[26%] top-[50%] w-[24%] aspect-square rounded-full shadow-xl border border-gray-100 overflow-hidden">
              {discOne.url ? (
                <img src={discOne.url} alt="disc" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full" style={{ background: `linear-gradient(135deg, #ffd0a6, ${sideColor})` }} />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[22%] aspect-square bg-gray-300 rounded-full" />
              </div>
            </div>

            {/* Legend */}
            <div className="absolute left-[16%] bottom-6 flex items-center gap-4">
              <StepLabel label="Upload box voorkant" icon="🖼️" />
              <span className="opacity-40">•</span>
              <StepLabel label="Upload disc labels" icon="💿" />
              <span className="opacity-40">•</span>
              <StepLabel label="Download PNG" icon="⬇️" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockupMaker;


